import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import ReactMarkdown from 'react-markdown';
import { DATA_PRIVACY, USER_AGREEMENT, PRIVACY_POLICY } from '@/src/legal';
import SecurityModal from './SecurityModal';
import TerminateAccountModal from './TerminateAccountModal';
import { useAuth } from '@/src/contexts/AuthContext';
import { 
  updateTermsOfService, 
  updatePrivacyPolicy, 
  updateArbitrationAgreement,
  fetchTermsOfService, 
  fetchPrivacyPolicy,
  fetchArbitrationAgreement
} from '@/src/services/legalService';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: string | null;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, initialView }) => {
  const [activeView, setActiveView] = useState<string | null>(initialView || null);
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);

  const { user, userProfile, triggerRecheckAcceptance, mapsQuota, refreshMapsQuota } = useAuth();
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'super_admin' || user?.email === 'jskiyemba@gmail.com';

  // Admin state management
  const [termsEditVersion, setTermsEditVersion] = useState('');
  const [termsEditDate, setTermsEditDate] = useState('');
  const [termsEditContent, setTermsEditContent] = useState('');

  const [privacyEditVersion, setPrivacyEditVersion] = useState('');
  const [privacyEditDate, setPrivacyEditDate] = useState('');
  const [privacyEditContent, setPrivacyEditContent] = useState('');

  const [arbitrationEditVersion, setArbitrationEditVersion] = useState('');
  const [arbitrationEditDate, setArbitrationEditDate] = useState('');
  const [arbitrationEditContent, setArbitrationEditContent] = useState('');

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Interactive privacy configurations
  const [personalizedIntel, setPersonalizedIntel] = useState(() => {
    return localStorage.getItem('privacy_personalized_intel') !== 'false';
  });
  const [retainHistory, setRetainHistory] = useState(() => {
    return localStorage.getItem('privacy_retain_history') !== 'false';
  });
  const [anonymizeGrounding, setAnonymizeGrounding] = useState(() => {
    return localStorage.getItem('privacy_anonymize_grounding') === 'true';
  });
  const [restrictLogs, setRestrictLogs] = useState(() => {
    return localStorage.getItem('privacy_restrict_logs') === 'true';
  });
  const [isWiped, setIsWiped] = useState(false);

  const handleTogglePersonalized = (val: boolean) => {
    setPersonalizedIntel(val);
    localStorage.setItem('privacy_personalized_intel', String(val));
  };

  const handleToggleRetainHistory = (val: boolean) => {
    setRetainHistory(val);
    localStorage.setItem('privacy_retain_history', String(val));
    if (!val) {
      localStorage.removeItem('recent_scans');
    }
  };

  const handleToggleAnonymize = (val: boolean) => {
    setAnonymizeGrounding(val);
    localStorage.setItem('privacy_anonymize_grounding', String(val));
  };

  const handleToggleRestrictLogs = (val: boolean) => {
    setRestrictLogs(val);
    localStorage.setItem('privacy_restrict_logs', String(val));
  };

  const handleWipeData = () => {
    setIsWiped(true);
    localStorage.removeItem('recent_scans');
    localStorage.removeItem('saved_ventures');
    localStorage.removeItem('app_active_tab');
    setTimeout(() => {
      setIsWiped(false);
    }, 2500);
  };

  // Fetch documents for admin editing and populate
  React.useEffect(() => {
    if (isOpen) {
      fetchTermsOfService().then(doc => {
        setTermsEditVersion(doc.version);
        setTermsEditDate(doc.effectiveDate);
        setTermsEditContent(doc.content);
      });
      fetchPrivacyPolicy().then(doc => {
        setPrivacyEditVersion(doc.version);
        setPrivacyEditDate(doc.effectiveDate);
        setPrivacyEditContent(doc.content);
      });
      fetchArbitrationAgreement().then(doc => {
        setArbitrationEditVersion(doc.version);
        setArbitrationEditDate(doc.effectiveDate);
        setArbitrationEditContent(doc.content);
      });
    }
  }, [isOpen]);

  // Reset activeView when modal opens if initialView is provided
  React.useEffect(() => {
    if (isOpen) {
      setActiveView(initialView || null);
      setPublishMessage(null);
      setPublishError(null);
    }
  }, [isOpen, initialView]);

  const menuItems = [
    { id: 'security', label: 'Sign in & security', icon: 'fa-shield-alt', action: () => setIsSecurityModalOpen(true) },
    { id: 'maps_quota', label: 'Geospatial & Maps Quota', icon: 'fa-map-location-dot', isCustomContent: true },
    { id: 'privacy', label: 'Data privacy', icon: 'fa-user-lock', content: DATA_PRIVACY },
    { id: 'agreement', label: 'User Agreement (Static)', icon: 'fa-file-contract', content: USER_AGREEMENT },
    { id: 'policy', label: 'Privacy Policy (Static)', icon: 'fa-file-shield', content: PRIVACY_POLICY },
    ...(isAdmin ? [
      { id: 'admin_terms', label: 'Manage Terms Of Service (Admin)', icon: 'fa-gavel', isCustomContent: true },
      { id: 'admin_privacy', label: 'Manage Privacy Policy (Admin)', icon: 'fa-user-cog', isCustomContent: true },
      { id: 'admin_arbitration', label: 'Manage Arbitration Agreement (Admin)', icon: 'fa-balance-scale', isCustomContent: true }
    ] : []),
    { id: 'terminate', label: 'Terminate Account', icon: 'fa-user-slash', danger: true, action: () => setIsTerminateModalOpen(true) },
  ];

  const handleClose = () => {
    setActiveView(null);
    onClose();
  };

  const handlePublishTerms = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPublishing(true);
    setPublishMessage(null);
    setPublishError(null);
    try {
      await updateTermsOfService(termsEditVersion, termsEditDate, termsEditContent);
      setPublishMessage(`Terms of Service successfully updated to v${termsEditVersion}. All users will be prompted to recheck.`);
      await triggerRecheckAcceptance();
    } catch (err: any) {
      setPublishError(err.message || 'Failed to update Terms of Service.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishPrivacy = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPublishing(true);
    setPublishMessage(null);
    setPublishError(null);
    try {
      await updatePrivacyPolicy(privacyEditVersion, privacyEditDate, privacyEditContent);
      setPublishMessage(`Privacy Policy successfully updated to v${privacyEditVersion}. All users will be prompted to recheck.`);
      await triggerRecheckAcceptance();
    } catch (err: any) {
      setPublishError(err.message || 'Failed to update Privacy Policy.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishArbitration = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPublishing(true);
    setPublishMessage(null);
    setPublishError(null);
    try {
      await updateArbitrationAgreement(arbitrationEditVersion, arbitrationEditDate, arbitrationEditContent);
      setPublishMessage(`Arbitration Agreement successfully updated to v${arbitrationEditVersion}. All users will be prompted to recheck.`);
      await triggerRecheckAcceptance();
    } catch (err: any) {
      setPublishError(err.message || 'Failed to update Arbitration Agreement.');
    } finally {
      setIsPublishing(false);
    }
  };

  const renderContent = () => {
    const item = menuItems.find(i => i.id === activeView);
    if (!item) return null;

    if (item.id === 'maps_quota') {
      const tierName = (userProfile?.subscriptionTier || mapsQuota?.tier || 'free').toUpperCase();
      const dm = mapsQuota?.categories?.dynamicMaps || { used: 0, limit: 5, remaining: 5 };
      const geo = mapsQuota?.categories?.geocoding || { used: 0, limit: 2, remaining: 2 };
      const auto = mapsQuota?.categories?.autocomplete || { used: 0, limit: 10, remaining: 10 };
      const dist = mapsQuota?.categories?.distanceMatrix || { used: 0, limit: 0, remaining: 0 };
      const period = mapsQuota?.billingPeriod || new Date().toISOString().slice(0, 7);

      const renderMeter = (title: string, icon: string, cat: { used: number; limit: number; remaining: number }, unit: string) => {
        const isUnlimited = cat.limit >= 999999;
        const pct = isUnlimited ? 0 : Math.min(100, Math.round((cat.used / Math.max(1, cat.limit)) * 100));
        return (
          <div className="p-4 border border-white/[0.06] bg-[#322D29] rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className={`fas ${icon} text-[#F26522] text-xs`}></i>
                <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading">{title}</span>
              </div>
              <span className="text-[10px] font-medium text-white/60">
                {cat.used} / {isUnlimited ? '∞' : cat.limit} {unit}
              </span>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${pct > 90 ? 'bg-rose-500' : pct > 60 ? 'bg-amber-500' : 'bg-[#F26522]'}`}
                style={{ width: `${isUnlimited ? 0 : pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[8px] font-medium text-white/40 uppercase">
              <span>{isUnlimited ? 'Unlimited Access' : `${cat.remaining} Remaining`}</span>
              <span>{isUnlimited ? 'Active' : `${pct}% Used`}</span>
            </div>
          </div>
        );
      };

      return (
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto bg-[#3a3430] text-white custom-scrollbar">
          <div className="flex items-center justify-between border-b border-white/[0.06] pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold uppercase text-[#F26522] tracking-widest">Geospatial Resource Monitor</span>
                <span className="px-2 py-0.5 bg-[#F26522]/15 border border-[#F26522]/30 text-[#F26522] text-[8px] font-bold uppercase tracking-wider rounded">{tierName} TIER</span>
              </div>
              <h3 className="text-sm font-bold uppercase tracking-tight text-white font-heading">
                Maps Platform Allocation ({period})
              </h3>
            </div>
            <button
              onClick={() => refreshMapsQuota()}
              className="px-3 py-1.5 border border-white/10 hover:bg-white/5 text-white/80 rounded-lg text-[9px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <i className="fas fa-rotate text-[8px]"></i>
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {renderMeter('Dynamic Map Views', 'fa-map', dm, 'loads')}
            {renderMeter('Geocoding Searches', 'fa-location-crosshairs', geo, 'queries')}
            {renderMeter('Places Autocomplete', 'fa-magnifying-glass-location', auto, 'sessions')}
            {renderMeter('Distance Matrix Proximity', 'fa-route', dist, 'elements')}
          </div>

          <div className="p-4 border border-[#F26522]/30 bg-[#F26522]/10 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading block">Need More Geospatial Bandwidth?</span>
              <p className="text-[11px] text-white/70 font-dm leading-snug">
                Expand to Premium, Optimize, or Team tiers for up to 3,000 pooled dynamic map views, automated distance matrices, and higher geocoding limits.
              </p>
            </div>
            <button
              onClick={() => {
                onClose();
                window.dispatchEvent(new CustomEvent('trigger-upgrade'));
              }}
              className="px-4 py-2.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[9px] font-bold uppercase tracking-widest shrink-0 rounded-lg transition-colors cursor-pointer font-heading"
            >
              View Tier Plans
            </button>
          </div>
        </div>
      );
    }

    if (item.id === 'admin_terms') {
      return (
        <form onSubmit={handlePublishTerms} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto bg-[#3a3430] text-white custom-scrollbar">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-wider">Document Control Panel (Firestore)</span>
            <h3 className="text-sm font-bold uppercase tracking-widest text-white font-heading">Edit Terms of Service</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-white/40">Target Version</label>
              <input 
                type="text" 
                required
                className="w-full px-3 py-2 text-xs font-medium bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] font-dm uppercase text-white placeholder-white/30"
                value={termsEditVersion}
                onChange={e => setTermsEditVersion(e.target.value)}
                placeholder="E.G. 1.1.0"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-white/40">Effective Date</label>
              <input 
                type="text" 
                required
                className="w-full px-3 py-2 text-xs font-medium bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] font-dm uppercase text-white placeholder-white/30"
                value={termsEditDate}
                onChange={e => setTermsEditDate(e.target.value)}
                placeholder="E.G. 2026, JUNE, 20TH"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading block">Markdown Content Body</label>
            <textarea
              required
              rows={10}
              className="w-full px-3 py-2 text-xs font-mono bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] text-white placeholder-white/30"
              value={termsEditContent}
              onChange={e => setTermsEditContent(e.target.value)}
            />
          </div>

          {publishMessage && (
            <div className="p-3 border border-emerald-500/40 bg-emerald-950/30 text-emerald-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
              {publishMessage}
            </div>
          )}

          {publishError && (
            <div className="p-3 border border-red-500/40 bg-red-950/30 text-red-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
              {publishError}
            </div>
          )}

          <button
            type="submit"
            disabled={isPublishing}
            className="w-full py-3.5 bg-[#F26522] text-white font-bold text-[10px] uppercase tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 disabled:opacity-50 transition-colors font-heading cursor-pointer"
          >
            {isPublishing ? 'Publishing Version...' : 'Deploy Revision & Require Re-acceptance'}
          </button>
        </form>
      );
    }

    if (item.id === 'admin_privacy') {
      return (
        <form onSubmit={handlePublishPrivacy} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto bg-[#3a3430] text-white custom-scrollbar">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-wider">Document Control Panel (Firestore)</span>
            <h3 className="text-sm font-bold uppercase tracking-widest text-white font-heading">Edit Privacy Policy</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-white/40">Target Version</label>
              <input 
                type="text" 
                required
                className="w-full px-3 py-2 text-xs font-medium bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] font-dm uppercase text-white placeholder-white/30"
                value={privacyEditVersion}
                onChange={e => setPrivacyEditVersion(e.target.value)}
                placeholder="E.G. 1.1.0"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-white/40">Effective Date</label>
              <input 
                type="text" 
                required
                className="w-full px-3 py-2 text-xs font-medium bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] font-dm uppercase text-white placeholder-white/30"
                value={privacyEditDate}
                onChange={e => setPrivacyEditDate(e.target.value)}
                placeholder="E.G. 2026, JUNE, 20TH"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading block">Markdown Content Body</label>
            <textarea
              required
              rows={10}
              className="w-full px-3 py-2 text-xs font-mono bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] text-white placeholder-white/30"
              value={privacyEditContent}
              onChange={e => setPrivacyEditContent(e.target.value)}
            />
          </div>

          {publishMessage && (
            <div className="p-3 border border-emerald-500/40 bg-emerald-950/30 text-emerald-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
              {publishMessage}
            </div>
          )}

          {publishError && (
            <div className="p-3 border border-red-500/40 bg-red-950/30 text-red-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
              {publishError}
            </div>
          )}

          <button
            type="submit"
            disabled={isPublishing}
            className="w-full py-3.5 bg-[#F26522] text-white font-bold text-[10px] uppercase tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 disabled:opacity-50 transition-colors font-heading cursor-pointer"
          >
            {isPublishing ? 'Publishing Version...' : 'Deploy Revision & Require Re-acceptance'}
          </button>
        </form>
      );
    }

    if (item.id === 'admin_arbitration') {
      return (
        <form onSubmit={handlePublishArbitration} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto bg-[#3a3430] text-white custom-scrollbar">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-wider">Document Control Panel (Firestore)</span>
            <h3 className="text-sm font-bold uppercase tracking-widest text-white font-heading">Edit Arbitration Agreement</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-white/40">Target Version</label>
              <input 
                type="text" 
                required
                className="w-full px-3 py-2 text-xs font-medium bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] font-dm uppercase text-white placeholder-white/30"
                value={arbitrationEditVersion}
                onChange={e => setArbitrationEditVersion(e.target.value)}
                placeholder="E.G. 1.0.0"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-white/40">Effective Date</label>
              <input 
                type="text" 
                required
                className="w-full px-3 py-2 text-xs font-medium bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] font-dm uppercase text-white placeholder-white/30"
                value={arbitrationEditDate}
                onChange={e => setArbitrationEditDate(e.target.value)}
                placeholder="E.G. 2026, JUNE, 15TH"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading block">Markdown Content Body</label>
            <textarea
              required
              rows={10}
              className="w-full px-3 py-2 text-xs font-mono bg-[#322D29] border border-white/[0.08] rounded-lg outline-none focus:border-[#F26522] text-white placeholder-white/30"
              value={arbitrationEditContent}
              onChange={e => setArbitrationEditContent(e.target.value)}
            />
          </div>

          {publishMessage && (
            <div className="p-3 border border-emerald-500/40 bg-emerald-950/30 text-emerald-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
              {publishMessage}
            </div>
          )}

          {publishError && (
            <div className="p-3 border border-red-500/40 bg-red-950/30 text-red-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
              {publishError}
            </div>
          )}

          <button
            type="submit"
            disabled={isPublishing}
            className="w-full py-3.5 bg-[#F26522] text-white font-bold text-[10px] uppercase tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 disabled:opacity-50 transition-colors font-heading cursor-pointer"
          >
            {isPublishing ? 'Publishing Version...' : 'Deploy Revision & Require Re-acceptance'}
          </button>
        </form>
      );
    }

    if (item.id === 'privacy') {
      return (
        <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto bg-[#3a3430] text-white space-y-6 custom-scrollbar">
          <div className="flex flex-col gap-1 border-b border-white/[0.06] pb-4">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-wider">Dynamic Regulatory & Session Preferences</span>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold uppercase tracking-widest text-white font-heading">Interactive Privacy Controls</h3>
              <span className="bg-[#F26522]/15 text-[#F26522] border border-[#F26522]/30 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">Active Sandbox</span>
            </div>
            <p className="text-[11px] text-white/60 font-dm leading-relaxed mt-1">
              Customize how eNvesta AI manages your local cache, biometric metadata, and cryptographic credentials in accordance with international data shields.
            </p>
          </div>

          <div className="space-y-3">
            {/* Control 1 */}
            <div className="flex items-start justify-between gap-4 p-4 border border-white/[0.06] bg-[#322D29] rounded-lg">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading block">Personalized Venture Intelligence</span>
                <span className="text-[11px] text-white/60 font-dm leading-tight block">Tailor scanning and feasibility results using historical micro-segments.</span>
              </div>
              <button 
                type="button"
                onClick={() => handleTogglePersonalized(!personalizedIntel)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${personalizedIntel ? 'bg-[#F26522]' : 'bg-white/20'}`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${personalizedIntel ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Control 2 */}
            <div className="flex items-start justify-between gap-4 p-4 border border-white/[0.06] bg-[#322D29] rounded-lg">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading block">Retain Scan History on Device</span>
                <span className="text-[11px] text-white/60 font-dm leading-tight block">Keep your search inputs and reconnaissance logs stored in browser local storage for instant loading.</span>
              </div>
              <button 
                type="button"
                onClick={() => handleToggleRetainHistory(!retainHistory)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${retainHistory ? 'bg-[#F26522]' : 'bg-white/20'}`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${retainHistory ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Control 3 */}
            <div className="flex items-start justify-between gap-4 p-4 border border-white/[0.06] bg-[#322D29] rounded-lg">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading block">Anonymize Search Groundings</span>
                <span className="text-[11px] text-white/60 font-dm leading-tight block">Scrub individual IP telemetry and device signatures when making requests to Google Maps and currency APIs.</span>
              </div>
              <button 
                type="button"
                onClick={() => handleToggleAnonymize(!anonymizeGrounding)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${anonymizeGrounding ? 'bg-[#F26522]' : 'bg-white/20'}`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${anonymizeGrounding ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Control 4 */}
            <div className="flex items-start justify-between gap-4 p-4 border border-white/[0.06] bg-[#322D29] rounded-lg">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading block">Restrict Clickstream Tracking</span>
                <span className="text-[11px] text-white/60 font-dm leading-tight block">Decline telemetry on navigation sequences, swot diagnostics, and layout state shifts.</span>
              </div>
              <button 
                type="button"
                onClick={() => handleToggleRestrictLogs(!restrictLogs)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${restrictLogs ? 'bg-[#F26522]' : 'bg-white/20'}`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${restrictLogs ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          <div className="border-t border-white/[0.06] pt-6 space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/80 font-heading block">Local Data Sandbox</span>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border border-red-500/30 bg-red-950/20 rounded-lg">
              <div className="space-y-1 max-w-sm text-left">
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-300 block">Wipe Dev-Sandbox Caches</span>
                <p className="text-[11px] text-red-200/80 font-dm leading-snug">
                  Permanently clear local storage bookmarks, scan history cache, and default preferences from this device.
                </p>
              </div>
              <button 
                type="button"
                onClick={handleWipeData}
                disabled={isWiped}
                className={`px-4 py-2.5 text-[9px] font-bold uppercase tracking-widest rounded-lg transition-all cursor-pointer ${isWiped ? 'bg-emerald-600 text-white' : 'bg-[#322D29] border border-white/10 text-white hover:bg-[#F26522]'}`}
              >
                {isWiped ? 'Sandbox Wiped !' : 'Purge Cached Files'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (item.content) {
      return (
        <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto bg-[#3a3430] text-white/80 prose prose-invert prose-sm max-w-none custom-scrollbar">
          <div className="markdown-body">
            <ReactMarkdown
              components={{
                a: ({ href, children, ...props }) => {
                  const isDpaLink = href === '/legal/dpa';
                  const isArbitrationLink = href === '/legal/arbitration';
                  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                    if (isDpaLink || isArbitrationLink) {
                      e.preventDefault();
                      const path = isDpaLink ? '/legal/dpa' : '/legal/arbitration';
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      const isWebView = typeof window !== 'undefined' && (
                        /wv|WebView/i.test(navigator.userAgent) ||
                        window.location.protocol === 'file:' ||
                        window.location.protocol === 'capacitor:' ||
                        window.location.protocol === 'chrome-extension:' ||
                        !(window.location.protocol.startsWith('http'))
                      );

                      if (isMobile || isWebView) {
                        window.location.href = `${window.location.origin}${path}`;
                      } else {
                        window.open(`${window.location.origin}${path}`, '_blank');
                      }
                    }
                  };
                  return (
                    <a
                      href={href}
                      onClick={handleClick}
                      className="text-[#F26522] hover:underline font-bold cursor-pointer"
                      {...props}
                    >
                      {children}
                    </a>
                  );
                }
              }}
            >
              {item.content}
            </ReactMarkdown>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="settings-modal-wrapper" className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/75"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-2xl bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col text-white"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/[0.06] bg-[#322D29] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                {activeView ? (
                  <button 
                    onClick={() => { setActiveView(null); setPublishMessage(null); setPublishError(null); }}
                    className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer text-white"
                  >
                    <i className="fas fa-arrow-left text-xs"></i>
                  </button>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-[#F26522] flex items-center justify-center text-white">
                    <i className="fas fa-cog text-sm"></i>
                  </div>
                )}
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-widest text-white font-heading">
                    {activeView ? menuItems.find(i => i.id === activeView)?.label : 'System Settings'}
                  </h2>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40 font-heading mt-0.5">
                    {activeView ? 'Legal Documentation' : 'Core Configuration'}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar bg-[#3a3430]">
              {activeView ? (
                renderContent()
              ) : (
                <div className="p-3 space-y-1">
                  {menuItems.map((item) => {
                    if (!item || !item.id) return null;
                    return (
                      <button
                        key={`menu-${item.id}`}
                        onClick={() => {
                          if (item.action) {
                            item.action();
                          } else {
                            setActiveView(item.id);
                          }
                        }}
                        className={`w-full flex items-center justify-between px-5 py-3.5 transition-all group cursor-pointer rounded-lg ${
                          item.danger 
                            ? 'hover:bg-red-950/30 text-red-400 hover:text-red-300' 
                            : 'hover:bg-white/5 text-white/80 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3.5">
                          <i className={`fas ${item.icon} text-xs ${item.danger ? 'text-red-400' : 'text-white/40 group-hover:text-[#F26522]'} transition-colors`}></i>
                          <span className="text-[10px] font-bold uppercase tracking-widest font-heading">
                            {item.label}
                          </span>
                        </div>
                        <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all translate-x-[-2px] group-hover:translate-x-0"></i>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {!activeView && (
              <div className="px-6 py-4 bg-[#322D29] border-t border-white/[0.06] flex flex-col items-center gap-1 shrink-0">
                <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/40 font-heading">
                  VERSION: 1.0.0.0
                </span>
                <span className="text-[8px] font-medium text-white/25 uppercase tracking-widest">
                  Build: Stable-Release-2026
                </span>
              </div>
            )}
          </motion.div>
        </div>
      )}
      
      <SecurityModal 
        isOpen={isSecurityModalOpen} 
        onClose={() => setIsSecurityModalOpen(false)} 
      />
      
      <TerminateAccountModal 
        isOpen={isTerminateModalOpen} 
        onClose={() => {
          setIsTerminateModalOpen(false);
          onClose(); // Close settings too
        }} 
      />
    </AnimatePresence>
  );
};

export default SettingsModal;
